import * as parserPhp from '@prettier/plugin-php/standalone';
import prettier from 'prettier';
import parserBabel from 'prettier/parser-babel';
import parserHtml from 'prettier/parser-html';
import { LANGUAGE_FORMATTER, SUPPORTED_PARSERS } from '../../../lib/formatter';
import topoSort from '../../../util/topoSort';
import { pricingModelValuesMap } from '../VaultSetupTokenConfigurator/constants';
import moment from 'moment';
import formatOneTimeCharges from '../../../util/formatOneTimeCharges';

const interpolateCode = (ecmEntry, replacements) => {
  const ecmEntryCopy = { ...ecmEntry };
  const regex = new RegExp(Object.keys(replacements).join('|'), 'g');
  ecmEntryCopy.code = ecmEntryCopy.code.replace(regex, (match) => replacements[match]);
  return ecmEntryCopy;
};

const processNestedBlocks = (snippets, codeStatus) => {
  const edges = snippets
    .filter((entry) => entry.key && entry.parentCodeBlock)
    .map((entry) => {
      return [entry.key, entry.parentCodeBlock];
    });
  // create the dependency graph
  const sortedEdges = topoSort(edges);

  sortedEdges.forEach((edge) => {
    inputNestedCodeBlock(snippets, codeStatus, edge);
  });
};

const formatCode = (files) => {
  files.forEach((file) => {
    const fileExtension = getFileExtension(file.filename);
    const parser = LANGUAGE_FORMATTER[fileExtension] || 'UNSUPPORTED';
    try {
      if (SUPPORTED_PARSERS.has(parser)) {
        file.code = prettier.format(file.code, {
          parser,
          plugins: [parserHtml, parserBabel, parserPhp],
          semi: true
        });
      }
      // TODO: Beautify backend files
      // if (LANGUAGE_FORMATTER[parser]) {
      //   file.code = beautify(file.code, BEAUTIFY_OPTS);
      // }
    } catch (error) {
      console.error('Prettier formatting failed:', error); // eslint-disable-line no-console
    }
  });
};

const addToCodeSnippet = (
  baseFile,
  codeSnippet,
  insertString, // string to insert based on
  regex, // regex to insert based on
  placeholderReplacement = false // indicates if we are replacing a placeholder such as '{captureOrder}',
) => {
  if (!baseFile.match(regex)) return baseFile;
  const splitBaseFile = baseFile.split(regex || insertString);
  splitBaseFile[0] += `\n${codeSnippet}\n`; // include a new line before and after the code snippet in all cases
  if (!placeholderReplacement) {
    splitBaseFile[0] += `${insertString}`;
  }
  const formatted = splitBaseFile.join('');
  return formatted;
};

const transformCardFieldsStyles = (cardFieldsConfigurator) => {
  const obj = {};
  const config = cardFieldsConfigurator.value || {};
  Object.keys(config).forEach((key) => {
    obj[`{${key}}`] = JSON.stringify(config[key] || {});
  });
  return obj;
};

const getFastlaneURL = (sdkBaseUrl, clientId) => {
  const sdkUrl = new URL('/sdk/js', sdkBaseUrl);
  const sdkParams = new URLSearchParams({
    'client-id': clientId,
    components: 'buttons,fastlane'
  });
  sdkUrl.search = sdkParams.toString();

  return sdkUrl.toString();
};

const inputNestedCodeBlock = (interpolatedBackendCode, codeStatus, childKey) => {
  // here we handle placeholders that are not in the base file, but in secondary code blocks
  const childBlock = interpolatedBackendCode.find((entry) => entry.key === childKey);
  const parentBlock = interpolatedBackendCode.find(
    (entry) => entry.key === childBlock?.parentCodeBlock
  );
  if (!childBlock || !parentBlock) return;
  const codeToUpdate = codeStatus[childKey] ? childBlock.code : '';
  const regex = new RegExp(`\\/\\*\\{\\s*${childKey}\\s*\\}\\*\\/`);
  parentBlock.code = parentBlock.code.replace(regex, codeToUpdate);
};

const constructBaseFrontend = (
  consolidatedFrontend,
  frontendLanguage,
  codeStatus,
  product,
  integrationType
) => {
  const baseFile = consolidatedFrontend.find((entry) => {
    // get the base HTML file which we will add all the callbacks to
    if (frontendLanguage === 'react') {
      return entry.key === 'baseReact';
    }
    if (integrationType) {
      return entry.type === integrationType && entry.key === 'baseHtml';
    }
    return entry.key === 'baseHtml';
  });
  if (!baseFile) return undefined;

  const result = [baseFile];
  const baseHtmlAppJs = consolidatedFrontend.find((entry) => {
    if (integrationType) {
      return entry.type === integrationType && entry.key === 'baseHtmlAppJs';
    }
    return entry.key === 'baseHtmlAppJs';
  });
  if (frontendLanguage === 'html') {
    result.push(baseHtmlAppJs);
  }

  const frontEndBaseTemplates = ['baseHtml', 'baseReact', 'baseHtmlAppJs', 'baseClientEnv'];
  consolidatedFrontend
    .filter((entry) => entry.key && !entry.parentCodeBlock) // parentCodeBlock means it is a nested entry
    .forEach((entry) => {
      if (entry.key.includes('base')) {
        if (!frontEndBaseTemplates.includes(entry.key)) {
          result.push(entry);
        }
        return;
      }
      if (product === 'fastlane') {
        // This is currently for fastlane.Scope can be expanded for more use cases.
        const regex = new RegExp(`\\/\\*\\{\\s*${entry.key}\\s*\\}\\*\\/*\n*`);
        const codeToUpdate = codeStatus[entry.key] ? entry.code : ``;
        const baseCode = entry.language === 'html' ? baseFile : baseHtmlAppJs;
        baseCode.code = addToCodeSnippet(
          baseCode.code,
          codeToUpdate,
          `/*{${entry.key}}*/`,
          regex,
          true /* placeholder replacement */
        );
      } else if (entry.key === 'initializeSdkViaScriptTag') {
        // this is always html and always baseHtml file
        baseFile.code = addToCodeSnippet(
          baseFile.code,
          entry.code,
          '</body>' /* string to replace */,
          undefined /* regex */,
          false /* placeholder replacement */
        );
      } else if (
        entry.key === 'handleBillingAddress' &&
        ['checkout.ejs', 'Index.cshtml', 'checkout.html', 'index.html'].includes(entry.filename)
      ) {
        const regex = new RegExp(`\\/\\*\\{\\s*${entry.key}\\s*\\}\\*\\/*\n*`);
        const codeToUpdate = codeStatus[entry.key] ? entry.code : ``;
        baseFile.code = addToCodeSnippet(
          baseFile.code,
          codeToUpdate,
          `/*{${entry.key}}*/`,
          regex,
          true /* placeholder replacement */
        );
      } else if (frontendLanguage === 'html') {
        const regex = /}\)\s*\.render\(/; // Regular expression to match '})' followed by '.render' with or without \n and spaces
        const codeToUpdate = codeStatus[entry.key] ? entry.code : '';
        baseHtmlAppJs.code = addToCodeSnippet(
          baseHtmlAppJs.code,
          codeToUpdate,
          '}).render(',
          regex,
          false /* placeholder replacement */
        );
      } else if (frontendLanguage === 'react') {
        const regex = new RegExp(`\\/\\*\\{\\s*${entry.key}\\s*\\}\\*\\/*\n*`);
        const codeToUpdate = codeStatus[entry.key] ? entry.code : ``;
        baseFile.code = addToCodeSnippet(
          baseFile.code,
          codeToUpdate,
          `/*{${entry.key}}*/`,
          regex,
          true /* placeholder replacement */
        );
      }
    });
  return result;
};

const constructBaseBackend = (consolidatedBackend, codeStatus) => {
  const baseFiles = consolidatedBackend.filter(
    (entry) => entry.key === 'baseNodeJs' || entry.isBaseFile
  );
  const baseFilesSet = new Set(Array.from(baseFiles).map((entry) => entry.key));
  const associatedFiles = consolidatedBackend.filter((entry) => !baseFilesSet.has(entry.key));

  associatedFiles.forEach((entry) => {
    const baseFile = baseFiles.find((base) => base.filename === entry.filename);
    const regex = new RegExp(`\\/\\*\\{\\s*${entry.key}\\s*\\}\\*\\/*\n*`);
    const codeToUpdate = codeStatus[entry.key] ? entry.code : '';
    baseFile.code = addToCodeSnippet(
      baseFile.code,
      codeToUpdate,
      undefined,
      regex /* regex */,
      true /* placeholder replacement */
    );
  });

  return baseFiles;
};

const getFileExtension = (filename) => {
  const fileParts = filename.split('.');
  const fileExtension = fileParts.length > 1 ? fileParts[fileParts.length - 1] : '';
  return fileExtension;
};

const getEntryTags = (entry) => {
  const fileExtension = getFileExtension(entry.filename);
  return fileExtension !== 'html'
    ? {
        start: `/*S_${entry.key}*/`,
        end: `/*E_${entry.key}*/`
      }
    : {
        start: `<!--S_${entry.key}-->`,
        end: `<!--E_${entry.key}-->`
      };
};

const getLineCount = (code) => (code.match(/\n/g) || []).length;

const initializeEntry = (entry) => {
  const { start, end } = getEntryTags(entry);

  return {
    ...entry,
    startLine: 0,
    endLine: 0,
    start,
    end,
    code: `${start}${entry.code}${end}`
  };
};

const filterByIntegrationType = (entry, integrationType) => {
  return entry.type === '' || entry.type === undefined || entry.type === integrationType;
};

const calculateEntryLines = (entry, files) => {
  const newEntry = entry;
  const { start, end } = entry;
  const file = files.find((currFile) => currFile.filename === entry.filename);
  if (!file) return newEntry;
  const startIndex = file.code.indexOf(start);
  const endIndex = file.code.indexOf(end);

  newEntry.startLine = getLineCount(file.code.substring(0, startIndex)) + 1;
  newEntry.endLine = getLineCount(file.code.substring(0, endIndex)) + 1;

  return newEntry;
};

const clearTags = (files) =>
  files.map((file) => ({
    ...file,
    code: file.code.replace(/( {1})?(<!--|\/\*)(S|E)_[A-Za-z0-9]+(\*\/|-->)( {1})?/g, '')
  }));

const mapEntryProperties = (entry) => ({
  ...entry,
  metadata: {
    active: entry.active,
    key: entry.key,
    language: entry.language,
    filename: entry.filename,
    startLine: entry.startLine,
    endLine: entry.endLine
  }
});

const handleFundingSourcesReplacements = (language, key, sources) => {
  if (!sources.length) return '';
  if (language === 'html') {
    return `&${key}=${sources}`;
  }
  if (language === 'react') return sources;
  return '';
};

export default function updateConsolidatedCode(
  frontend = [],
  backend = [],
  {
    sdkBaseUrl,
    clientId,
    clientAccessToken,
    buyerCountry,
    currency,
    intent,
    enableFunding,
    disableFunding
  } = {} /* SDK settings */,
  { frontend: frontendLanguage, backend: backendLanguage, status: codeStatus } /* code language */,
  {
    amount: { value } = {},
    threeDsSetting = '',
    contactPreference,
    contactData = {}
  } = {} /* create order settings */,
  { buttonSettings: { color, label, shape, layout } = {} } = {},
  cardFieldsConfigurator = {},
  product = '',
  type = '',
  {
    name: vaultPlanName,
    selectedUsagePattern,
    selectedFrequency,
    intervalCount,
    totalCycles,
    startDate,
    oneTimeCharges,
    shipping,
    paymentTokens
  } = {} /* Vault setup token settings */,
  geoLocOverride
) {
  const result = [];
  const frontEndTemplatesToInterpolate = new Set([
    'baseHtml',
    'initializeSdkViaScriptTag',
    'configureButtons',
    'configureButtonMessages',
    'baseHtmlAppJs',
    'baseReact',
    'onApprove',
    'acdcPayPalCardFields',
    'specifyLocale'
  ]);
  // frontend
  let interpolatedFrontendCode = frontend
    .filter((entry) => {
      if (frontendLanguage === 'html') {
        return entry.language === 'html' || entry.language === 'javascript';
      }

      return entry.language === frontendLanguage;
    })
    .filter((entry) => filterByIntegrationType(entry, type))
    .map(initializeEntry)
    .map((entry) => {
      if (frontEndTemplatesToInterpolate.has(entry.key)) {
        let replacements = {
          '{buyer_country}': buyerCountry,
          '{buyer_currency}': currency,
          '{client_id}': clientId,
          '{button_color}': color,
          '{button_label}': label,
          '{button_shape}': shape,
          '{button_layout}': layout,
          '{order_intent}': intent,
          '{order_amount}': value,
          '{enable_funding}': handleFundingSourcesReplacements(
            entry.language,
            'enable-funding',
            enableFunding
          ),
          '{disable_funding}': handleFundingSourcesReplacements(
            entry.language,
            'disable-funding',
            disableFunding
          ),
          ...transformCardFieldsStyles(cardFieldsConfigurator),
          '{geoLocOverride}': geoLocOverride
        };

        if (product === 'fastlane') {
          replacements = {
            ...replacements,
            '{client_access_token}': clientAccessToken,
            '{sdk_url}': getFastlaneURL(sdkBaseUrl, clientId)
          };
        }

        return interpolateCode(entry, replacements);
      }

      return { ...entry };
    });

  processNestedBlocks(interpolatedFrontendCode, codeStatus);

  let newConsolidatedFrontend = constructBaseFrontend(
    interpolatedFrontendCode,
    frontendLanguage,
    codeStatus,
    product,
    type
  );
  if (newConsolidatedFrontend) {
    formatCode(newConsolidatedFrontend);
    interpolatedFrontendCode = interpolatedFrontendCode.map((entry) =>
      calculateEntryLines(entry, newConsolidatedFrontend)
    );
    newConsolidatedFrontend = clearTags(newConsolidatedFrontend);
    newConsolidatedFrontend = newConsolidatedFrontend.map((file) => ({
      ...file,
      metadata: interpolatedFrontendCode
        .filter((codeBlock) => codeBlock.filename === file.filename)
        .map(mapEntryProperties)
    }));

    result.push(...newConsolidatedFrontend);
  }
  // backend
  let interpolatedBackendCode = backend
    .filter((entry) => {
      return entry.language === backendLanguage;
    })
    .filter((entry) => filterByIntegrationType(entry, type))
    .map(initializeEntry)
    .map((entry) => {
      if (
        entry.key === 'createOrderPayload' ||
        entry.key === 'handleShippingAddress' ||
        entry.key === '3dsConfiguration' ||
        entry.key === 'contactModule' ||
        entry.key === 'shippingWrapper' ||
        entry.key === 'setUpToken' ||
        entry.key === 'contactPreference'
      ) {
        // add to json
        const replacements = {
          '{shipping_currency}': `${currency}`,
          '{order_currency_code}': `${currency}`,
          '{order_value}': `${value}`,
          '{order_intent}': `${intent.toUpperCase()}`,
          '{3ds_setting}': threeDsSetting,
          '{contact_preference}': contactPreference,
          '{country_code}': contactData?.shipping?.phone_number?.country_code,
          '{national_number}': contactData?.shipping?.phone_number?.national_number,
          '{email_address}': contactData?.shipping?.email_address,
          '{usage_pattern}': selectedUsagePattern,
          '{shipping}': shipping ? `shipping: ${JSON.stringify(shipping, null, 2)}` : '',
          '{pricing_model}': pricingModelValuesMap[selectedUsagePattern],
          '{frequency}': selectedFrequency,
          '{interval_count}': intervalCount,
          '{total_cycles}': totalCycles,
          '{start_date}': moment(startDate).format('YYYY-MM-DD'),
          '{one_time_charges}': JSON.stringify(
            formatOneTimeCharges(oneTimeCharges, currency),
            null,
            2
          ),
          '{plan_name}': vaultPlanName,
          '{payment_token}': paymentTokens?.selected || 'PAYMENT-TOKEN-ID'
        };
        return interpolateCode(entry, replacements);
      }
      return { ...entry };
    });

  processNestedBlocks(interpolatedBackendCode, codeStatus);

  let newConsolidatedBackend = constructBaseBackend(
    interpolatedBackendCode.filter((entry) => !entry.parentCodeBlock),
    codeStatus
  );
  if (newConsolidatedBackend) {
    formatCode(newConsolidatedBackend);
    interpolatedBackendCode = interpolatedBackendCode.map((entry) =>
      calculateEntryLines(entry, newConsolidatedBackend)
    );
    newConsolidatedBackend = clearTags(newConsolidatedBackend);
    newConsolidatedBackend = newConsolidatedBackend.map((file) => ({
      ...file,
      metadata: interpolatedBackendCode
        .filter((codeBlock) => codeBlock.filename === file.filename)
        .map(mapEntryProperties)
    }));
    result.push(...newConsolidatedBackend);
  }
  return result;
}
